import type { Persona } from '@/utils/pocketbase';
import { extractPersonaName } from '@/utils/markdown';
import { useTranslation } from 'react-i18next';

interface TooltipState {
  id: string;
  type: 'edit' | 'delete' | 'select';
}

interface PersonaListItemProps {
  persona: Persona;
  projectName: string;
  isActive: boolean;
  isDisabled: boolean;
  tooltipVisible: TooltipState | null;
  onSelect: (persona: Persona) => void;
  onTooltipEnter: (state: TooltipState) => void;
  onTooltipLeave: () => void;
}

export function PersonaListItem({
  persona,
  projectName,
  isActive,
  isDisabled,
  tooltipVisible,
  onSelect,
  onTooltipEnter,
  onTooltipLeave,
}: PersonaListItemProps) {
  const { t } = useTranslation('personas');
  const personaName = extractPersonaName(persona);

  return (
    <button
      key={persona.id}
      onClick={() => !isDisabled && onSelect(persona)}
      onMouseEnter={() =>
        isDisabled && onTooltipEnter({ id: persona.id, type: 'select' })
      }
      onMouseLeave={onTooltipLeave}
      disabled={isDisabled}
      className="relative"
    >
      <div
        className={`${
          isActive
            ? 'bg-[rgba(33,151,241,0.2)]'
            : isDisabled
              ? 'bg-[#f4f4f3] cursor-not-allowed'
              : 'bg-[#f4f4f3] hover:bg-[#eeeeee]'
        } rounded-[16px] px-[24px] py-[16px] flex items-center gap-[16px] transition-colors overflow-hidden`}
      >
        <p
          className={`flex-1 font-segoe text-[16px] leading-[24px] ${isDisabled ? 'text-[#cecdc8]' : 'text-[#0d0d0d]'} text-left truncate`}
        >
          {projectName} - {personaName}
        </p>

        <div className="w-[24px] h-[24px] flex-shrink-0 rotate-90 -scale-y-100">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path
              d="M19.92 8.95L13.4 15.47C12.63 16.24 11.37 16.24 10.6 15.47L4.08 8.95"
              stroke={isDisabled ? '#cecdc8' : '#0d0d0d'}
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeMiterlimit="10"
            />
          </svg>
        </div>
      </div>

      {/* Tooltip for disabled persona */}
      {tooltipVisible?.id === persona.id && isDisabled && (
        <div className="absolute top-1/2 left-[calc(100%+16px)] -translate-y-1/2 bg-[rgba(13,13,13,0.8)] px-[24px] py-[12px] rounded-[16px] whitespace-nowrap z-10">
          <p className="font-segoe text-[14px] leading-[20px] text-white text-center">
            {t('tooltip.paidOnly')}
          </p>
        </div>
      )}
    </button>
  );
}
